import type { Maybe } from '@adornis/base/utilTypes.js';
import type { Form } from '@adornis/forms/form.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { computed } from '@lit-labs/preact-signals';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { html, nothing } from 'lit';
import { InterviewAPI } from '../../interviews/api/InterviewAPI.js';
import type { InterviewSlot } from '../../interviews/entities/InterviewSlot.js';
import type { RegisterStudentData } from '../api/registerStudentWithSuSForm.js';

export interface MappedUITimeSlot {
  id: string;
  day: string | undefined;
  date: string | undefined;
  time: string;
  studentOccupied: boolean;
  schoolID: Maybe<string>;
}

export const SGSusEntryFormInterviewSlots = stateful(
  (props: {
    schoolID: string;
    form: Form<RegisterStudentData>;
    /** The selected slot's data, so the review can show it without a second subscription of its own. */
    onSelected?: (slot: Maybe<InterviewSlot>) => void;
  }) => {
    const [selectedSlotId, setSelectedSlotId] = useState<string | null>(
      props.form.document?.selectedInterviewSlotID ?? null,
    );

    const {
      value: slots,
      loading: slotsLoading,
      error: slotsError,
    } = useQuery(
      () =>
        InterviewAPI.subscribePublicSlotsForSchool(props.schoolID)({
          _id: 1,
          _class: 1,
          schoolID: 1,
          startDate: 1,
          endDate: 1,
          childID: 1,
        }),
      [props.schoolID],
    );

    useEffect(() => {
      if (
        !slotsLoading &&
        slots &&
        selectedSlotId &&
        !slots.some(slot => slot._id === selectedSlotId && !slot.childID)
      ) {
        setSelectedSlotId(null);
        props.form.pickValueForField(['selectedInterviewSlotID'], null);
      }
    }, [slots, slotsLoading]);
    useEffect(() => {
      props.onSelected?.(slots?.find(slot => slot._id === selectedSlotId) ?? null);
    }, [slots, selectedSlotId]);

    const timeSlots = computed(() => {
      if (slotsLoading || !slots) {
        return [];
      }

      return slots
        .filter(slot => !slot.childID) // Only show available slots (not occupied by students)
        .map(slot => ({
          id: slot._id,
          day: slot.startDate ? format(slot.startDate, 'EEEE', { locale: de }) : undefined,
          date: slot.startDate ? format(slot.startDate, 'dd.MM.yyyy') : undefined,
          time: `${slot.startDate ? format(slot.startDate, 'HH:mm') : ''} - ${slot.endDate ? format(slot.endDate, 'HH:mm') : ''}`,
          studentOccupied: !!slot.childID,
          schoolID: slot.schoolID,
        }));
    });

    const groupedSlots = computed(() =>
      timeSlots.value.reduce<Record<string, Record<string, MappedUITimeSlot[]>>>((groups, slot) => {
        if (!slot.date || !slot.time) return groups;
        const dayHash = slot.date;
        const timeHash = slot.time;

        const daySlots = groups[dayHash] ?? {};
        const timeSlots = daySlots[timeHash] ?? [];
        return {
          ...groups,
          [dayHash]: {
            ...daySlots,
            [timeHash]: [...timeSlots, slot],
          },
        };
      }, {}),
    );

    const handleSlotSelection = (availableSlotsIDs: string[]) => {
      const slotId = availableSlotsIDs[0];
      if (!slotId) return;
      setSelectedSlotId(slotId);
      props.form.pickValueForField(['selectedInterviewSlotID'], slotId);
    };

    const renderSlot = (time: string, slots: MappedUITimeSlot[]) => {
      const selectedSlot = slots.find(slot => slot.id === selectedSlotId);
      const isSelected = selectedSlot !== undefined;

      const available = slots.every(slot => !slot.studentOccupied);
      const availableSlotsIDs = slots.filter(slot => !slot.studentOccupied).map(slot => slot.id);

      return html`
        <button
          type="button"
          aria-pressed=${isSelected}
          class="card min-h-11 text-left ${
            isSelected ? 'border-primary bg-primary/5' : 'border-base-300'
          } cursor-pointer hover:bg-base-200 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          @click=${() => handleSlotSelection(availableSlotsIDs)}
        >
          <div class="card-body p-3 gap-2">
            <div class="font-semibold">${time}</div>
            <div class="flex justify-between items-center text-sm">
              <span class="font-medium"> ${available ? 'Verfügbar' : 'Nicht verfügbar'} </span>
            </div>
            ${isSelected ? html` <div class="text-sm mt-1 font-medium">✓ Ausgewählt</div> ` : ''}
          </div>
        </button>
      `;
    };

    const renderDayColumn = (day: string, slots: Record<string, MappedUITimeSlot[]>) => html`
      <div class="flex flex-col gap-3">
        <div class="text-center">
          <h3 class="font-semibold">${day}</h3>
        </div>
        <div class="flex flex-col gap-2">${Object.keys(slots).map(time => renderSlot(time, slots[time] ?? []))}</div>
      </div>
    `;

    if (slotsLoading) return html`<p role="status">Gesprächstermine werden geladen …</p>`;
    if (slotsError)
      return html`<p class="alert alert-warning alert-soft text-base-content" role="alert">
        Gesprächstermine konnten nicht geladen werden. Bitte versuchen Sie es später erneut.
      </p>`;
    if (!Object.keys(groupedSlots.value).length)
      return html`<section class="card">
        <div class="card-body p-4 sm:p-6">
          <h2 class="font-semibold md:text-lg">Aufnahmegespräch</h2>
          <p>
            Aktuell sind keine Gesprächstermine verfügbar. Für diese Bewerbung ist ein Gesprächstermin erforderlich.
            Bitte versuchen Sie es später erneut oder wenden Sie sich an das Sekretariat. Ihr Entwurf bleibt auf diesem
            Gerät gespeichert.
          </p>
        </div>
      </section>`;

    return html`
      <div class="flex flex-col gap-4 w-full">
        <div class="card w-full">
          <div class="card-body gap-5 p-4 sm:p-6">
            <h2 class="font-semibold md:text-lg">Terminauswahl für das Aufnahmegespräch</h2>
            <p>Wählen Sie einen verfügbaren Termin für das Aufnahmegespräch aus.</p>

            ${
              selectedSlotId
                ? html`
                    <div role="alert" class="alert alert-success alert-soft text-base-content">
                      <span
                        class="icon-[material-symbols--check-circle-rounded] shrink-0 text-xl"
                        aria-hidden="true"
                      ></span>
                      <span>
                        Termin ausgewählt: ${timeSlots.value.find(s => s.id === selectedSlotId)?.day}
                        ${timeSlots.value.find(s => s.id === selectedSlotId)?.date},
                        ${timeSlots.value.find(s => s.id === selectedSlotId)?.time}
                      </span>
                    </div>
                  `
                : nothing
            }

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              ${[...Object.entries(groupedSlots.value)].map(([day, slots]) => renderDayColumn(day, slots))}
            </div>
          </div>
        </div>
      </div>
    `;
  },
);
